import { create } from 'zustand';
import { releaseApi } from '../services/api';
import type { Release } from '../types';

interface ReleaseState {
  releases: Release[];
  loading: boolean;
  error: string | null;

  fetchReleases: () => Promise<void>;
  createRelease: (data: Partial<Release>) => Promise<Release>;
  updateRelease: (id: string, data: Partial<Release>) => Promise<Release>;
  deleteRelease: (id: string) => Promise<void>;
  regenerateNotes: (id: string) => Promise<string>;
}

export const useReleaseStore = create<ReleaseState>((set) => ({
  releases: [],
  loading: false,
  error: null,

  fetchReleases: async () => {
    set({ loading: true, error: null });
    try {
      const releases = await releaseApi.list();
      set({ releases, loading: false });
    } catch (err: any) {
      set({ error: err.message, loading: false });
    }
  },

  createRelease: async (data) => {
    const release = await releaseApi.create(data);
    set((s) => ({ releases: [...s.releases, release] }));
    return release;
  },

  updateRelease: async (id, data) => {
    const release = await releaseApi.update(id, data);
    set((s) => ({
      releases: s.releases.map((r) => (r._id === id ? { ...r, ...release } : r)),
    }));
    return release;
  },

  deleteRelease: async (id) => {
    await releaseApi.delete(id);
    set((s) => ({ releases: s.releases.filter((r) => r._id !== id) }));
  },

  regenerateNotes: async (id) => {
    const result = await releaseApi.regenerateNotes(id);
    // Update the notes in local state
    set((s) => ({
      releases: s.releases.map((r) => (r._id === id ? { ...r, notes: result.notes } : r)),
    }));
    return result.notes;
  },
}));
